import { useMemo } from 'react';
import PropTypes from 'prop-types';

import TabbedDetailCard from '#/modules/commonComponents/layout/TabbedDetailCard';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import FmsReadonlyTable from '#/modules/finMgmt/shared/components/FmsReadonlyTable';
import VoucherStatusBadge from '#/modules/finMgmt/vouchers/components/VoucherStatusBadge';
import { formatVoucherTypeLabel } from '#/modules/finMgmt/vouchers/helper/voucherStatus';
import { formatDateOrDash, formatDateTimeOrDash } from '#/utils/date';
import { formatFmsAmount } from '#/modules/finMgmt/shared/helper/fmsAmount';
import { formatDisplayAmount, formatDisplayQty } from '#/utils/formatNumber';

import { formatPaymentRequestStatusLabel } from '../helper/paymentRequestStatus';

function displayDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function textColumn(id, header, field, size) {
  return {
    id,
    header,
    accessorFn: (row) => displayDash(row?.[field]),
    size,
    enableSorting: false,
    meta: { cellClassName: 'td_left' },
    cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
  };
}

function numberColumn(id, header, accessorFn, size) {
  return {
    id,
    header,
    accessorFn,
    size,
    enableSorting: false,
    meta: { cellClassName: 'td_right' },
  };
}

function buildLineColumns() {
  return [
    textColumn('itemName', '品項', 'itemName', 220),
    textColumn('purpose', '用途', 'purpose', 240),
    numberColumn('unitPrice', '單價', (row) => formatDisplayAmount(row?.unitPrice), 120),
    numberColumn('qty', '數量', (row) => formatDisplayQty(row?.qty), 100),
    numberColumn('lineAmount', '金額', (row) => formatFmsAmount(row?.lineAmount), 130),
  ];
}

function buildVoucherColumns(getVoucherDetailPath) {
  return [
    {
      id: 'voucherNo',
      header: '憑證號碼',
      accessorFn: (row) => displayDash(row?.voucherNo),
      size: 160,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={displayDash(row.original.voucherNo)}
          detailPath={row.original.id ? getVoucherDetailPath(row.original.id) : null}
          tooltipContent="雙擊開啟憑證"
        />
      ),
    },
    {
      id: 'voucherType',
      header: '類型',
      accessorFn: (row) => formatVoucherTypeLabel(row?.voucherType),
      size: 90,
      enableSorting: false,
    },
    {
      id: 'voucherDate',
      header: '憑證日期',
      accessorFn: (row) => formatDateOrDash(row?.voucherDate),
      size: 120,
      enableSorting: false,
    },
    numberColumn('totalAmount', '總額', (row) => formatFmsAmount(row?.totalAmount), 120),
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        row.original.status
          ? <VoucherStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
    },
    textColumn('remark', '備註', 'remark', 200),
  ];
}

function buildHistoryColumns() {
  return [
    {
      id: 'changedAt',
      header: '時間',
      accessorFn: (row) => formatDateTimeOrDash(row?.changedAt),
      size: 170,
      enableSorting: false,
    },
    {
      id: 'fromStatus',
      header: '原狀態',
      accessorFn: (row) => (
        row?.fromStatus ? formatPaymentRequestStatusLabel(row.fromStatus) : '—'
      ),
      size: 110,
      enableSorting: false,
    },
    {
      id: 'toStatus',
      header: '新狀態',
      accessorFn: (row) => formatPaymentRequestStatusLabel(row?.toStatus),
      size: 110,
      enableSorting: false,
    },
    textColumn('reason', '原因', 'reason', 240),
    // BE 目前只回 changedBy UUID（無姓名），先原樣顯示
    textColumn('changedBy', '操作人', 'changedBy', 200),
  ];
}

const PaymentRequestDetailTabs = ({ detail, getVoucherDetailPath }) => {
  const lineColumns = useMemo(() => buildLineColumns(), []);
  const voucherColumns = useMemo(
    () => buildVoucherColumns(getVoucherDetailPath),
    [getVoucherDetailPath],
  );
  const historyColumns = useMemo(() => buildHistoryColumns(), []);
  const history = useMemo(
    () => [...(detail?.history ?? [])].sort(
      (a, b) => String(b?.changedAt ?? '').localeCompare(String(a?.changedAt ?? '')),
    ),
    [detail?.history],
  );

  return (
    <TabbedDetailCard defaultValue="lines">
      <TabbedDetailCard.List>
        <TabbedDetailCard.Trigger value="lines">請款明細</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="vouchers">引用憑證</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="history">狀態歷程</TabbedDetailCard.Trigger>
      </TabbedDetailCard.List>

      <TabbedDetailCard.Pane value="lines" title="請款明細">
        <FmsReadonlyTable
          rows={detail?.lines ?? []}
          columns={lineColumns}
          rowIdPrefix="payment-request-line"
          emptyText="尚無明細"
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane value="vouchers" title="引用憑證">
        <FmsReadonlyTable
          rows={detail?.vouchers ?? []}
          columns={voucherColumns}
          rowIdPrefix="payment-request-voucher"
          emptyText="尚未引用憑證"
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane value="history" title="狀態歷程">
        <FmsReadonlyTable
          rows={history}
          columns={historyColumns}
          rowIdPrefix="payment-request-history"
          emptyText="尚無狀態歷程"
        />
      </TabbedDetailCard.Pane>
    </TabbedDetailCard>
  );
};

PaymentRequestDetailTabs.propTypes = {
  detail: PropTypes.shape({
    lines: PropTypes.arrayOf(PropTypes.object),
    vouchers: PropTypes.arrayOf(PropTypes.object),
    history: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  getVoucherDetailPath: PropTypes.func.isRequired,
};

export default PaymentRequestDetailTabs;
